import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import CardEditor from "@/pages/CardEditor";
import CRM from "@/pages/CRM";
import Conversations from "@/pages/Conversations";
import Stats from "@/pages/Stats";
import Appointments from "@/pages/Appointments";
import AdminDistributors from "@/pages/AdminDistributors";
import AdminClients from "@/pages/AdminClients";
import AdminWaitlist from "@/pages/AdminWaitlist";
import Legal from "@/pages/Legal";
import CookieBanner from "@/components/xuma/CookieBanner";
import DistributorPanel from "@/pages/DistributorPanel";
import RefLanding from "@/pages/RefLanding";
import PaymentSuccess from "@/pages/PaymentSuccess";
import ChangePassword from "@/pages/ChangePassword";
import PublicCard from "@/pages/PublicCard";
import AuthCallback from "@/pages/AuthCallback";

function Loader() {
  return (
    <div className="min-h-screen bg-xuma-bg grid place-items-center">
      <div className="h-10 w-10 rounded-full border-2 border-xuma-primary border-t-transparent animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && window.location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Handle Google OAuth callback before anything else (synchronous during render).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/editor" element={<ProtectedRoute><CardEditor /></ProtectedRoute>} />
      <Route path="/crm" element={<ProtectedRoute><CRM /></ProtectedRoute>} />
      <Route path="/conversations" element={<ProtectedRoute><Conversations /></ProtectedRoute>} />
      <Route path="/stats" element={<ProtectedRoute><Stats /></ProtectedRoute>} />
      <Route path="/appointments" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
      <Route path="/admin/distributors" element={<ProtectedRoute><AdminDistributors /></ProtectedRoute>} />
      <Route path="/admin/clients" element={<ProtectedRoute><AdminClients /></ProtectedRoute>} />
      <Route path="/admin/waitlist" element={<ProtectedRoute><AdminWaitlist /></ProtectedRoute>} />
      <Route path="/distributor" element={<ProtectedRoute><DistributorPanel /></ProtectedRoute>} />
      <Route path="/c/:slug" element={<PublicCard />} />
      <Route path="/r/:refCode" element={<RefLanding />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/legal/:slug" element={<Legal />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <CookieBanner />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
